import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material'

/**
 * Lo que queda en pie cuando una pantalla se rompe.
 *
 * ── POR QUÉ EXISTE ──────────────────────────────────────────────────────────
 * Esta app no tenía ninguno. Con React 19 una excepción en render que nadie
 * atrapa DESMONTA LA RAÍZ ENTERA: se va la barra de secciones, la de pestañas,
 * la cabecera y el `<main>` que es el único contenedor que hace scroll. El
 * operador ve una página en blanco, sin mensaje y sin nada que reportar más que
 * «se puso en blanco». Una sola fila con un campo nulo en cualquiera de las
 * veinte pantallas bastaba.
 *
 * ── POR QUÉ VA CON `key` ────────────────────────────────────────────────────
 * Un boundary de clase se queda en `hasError` para siempre. Montado sin `key`
 * dentro de `SectionLayout`, la primera pantalla rota dejaría a TODAS las
 * pestañas de esa sección mostrando el error, y sólo un recargado lo limpiaría
 * — sería peor que no tenerlo. `key={pathname}` lo vuelve a montar en cada
 * navegación, así que el fallo dura lo que dura la pantalla que lo causó.
 *
 * ── DOS FALLOS DISTINTOS, DOS SALIDAS DISTINTAS ─────────────────────────────
 * Un `import()` rechazado también llega aquí, atravesando `<Suspense>`, y no es
 * un defecto: es el despliegue de hace un minuto invalidando en CloudFront el
 * chunk que este navegador todavía no había pedido. Ahí la acción correcta es
 * RECARGAR, no reintentar el render — el archivo ya no existe. Un error de
 * render sí se reintenta, porque suele depender de datos que van a volver a
 * pedirse.
 */

/**
 * Un chunk que ya no está en el origen.
 *
 * Los tres navegadores redactan el mensaje distinto y ninguno expone un código,
 * así que se reconoce por texto — con la lista de los tres, no la de uno.
 */
function isChunkLoadError(error: Error): boolean {
  const s = `${error.name} ${error.message}`
  return (
    /Failed to fetch dynamically imported module/i.test(s) || // Chrome, Edge
    /error loading dynamically imported module/i.test(s) || // Firefox
    /Importing a module script failed/i.test(s) || // Safari
    /ChunkLoadError|Loading chunk \d+ failed/i.test(s) // bundlers antiguos
  )
}

/**
 * Un código corto y ESTABLE para que dos reportes del mismo fallo se puedan
 * juntar.
 *
 * No es aleatorio a propósito: un código nuevo en cada render haría que el mismo
 * defecto llegara como cinco incidencias distintas. Sale del mensaje, que es lo
 * único que no cambia entre una sesión y otra. Ocho caracteres, como
 * `incidentCode()` recorta el `traceId` del servidor, para que se lean igual.
 */
function codeFor(error: Error): string {
  const s = `${error.name}:${error.message}`
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return (h >>> 0).toString(16).padStart(8, '0')
}

type Props = {
  children: ReactNode
  /** Qué se rompió, en palabras del operador: «esta pantalla», «la consola». */
  scope: string
}

type State = { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // A la consola del navegador, con el árbol de componentes: es lo que
    // convierte «se puso en blanco» en un reporte accionable.
    console.error(`[${codeFor(error)}] ${this.props.scope}:`, error, info.componentStack)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    if (isChunkLoadError(error)) {
      return (
        <Alert
          severity="info"
          variant="outlined"
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Recargar
            </Button>
          }
        >
          <AlertTitle>Hay una versión nueva</AlertTitle>
          Se desplegó la consola mientras tenías esto abierto, así que este pedazo de la aplicación
          ya no está donde tu navegador lo buscaba. Recargar lo arregla; no se pierde nada.
        </Alert>
      )
    }

    return (
      <Alert
        severity="error"
        variant="outlined"
        action={
          <Button color="inherit" size="small" onClick={() => this.setState({ error: null })}>
            Reintentar
          </Button>
        }
      >
        <AlertTitle>No se pudo dibujar {this.props.scope}</AlertTitle>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          El resto de la consola sigue funcionando. Si vuelve a pasar, manda el código de abajo:
          es lo que ata este fallo a lo que quedó en la bitácora del navegador.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'var(--font-mono)' }}>
          {error.name} · Traza {codeFor(error)}
        </Typography>
        {/* Plegado: el stack es para quien lo pidió, no la primera cosa que el
            operador tiene que leer. Es la única razón por la que `Panel` no
            tiene un quinto estado con un stack dentro. */}
        <Box component="details" sx={{ mt: 1 }}>
          <Box component="summary" sx={{ cursor: 'pointer', fontSize: 12 }}>
            Detalle técnico
          </Box>
          <Box
            component="pre"
            sx={{
              mt: 1,
              p: 1,
              fontSize: 11,
              lineHeight: 1.4,
              overflowX: 'auto',
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            {error.message}
            {error.stack ? `\n\n${error.stack}` : ''}
          </Box>
        </Box>
      </Alert>
    )
  }
}
